/**
 * TrialBillingCard — Account & Billing's trial card (TRIAL_PLAN.md §8.8).
 * Status, the end date, the itemised day-14 preview with per-item removal,
 * cancel, and start-paying-early.
 *
 * A CANCELLED trial is unambiguous here (Randy, Oct 2026): the Pro selections
 * are shown as VOID and explicitly will-not-be-charged, never as pending
 * selections waiting to bill.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Clock, X, ArrowSquareOut, Lock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useTrial, setTrial, money, trialDate } from "@/lib/trial";
import TrialCreditNote from "@/components/TrialCreditNote";
import TrialStartCard from "@/components/TrialStartCard";

export default function TrialBillingCard() {
  const { trial } = useTrial();
  const [busy, setBusy] = useState(null);

  if (!trial) return null;
  // Not started and allowed to → the offer itself (§8.2b).
  if (!trial.live && trial.eligible) {
    return <div data-testid="billing-trial-section"><TrialStartCard variant="billing" /></div>;
  }
  const over = ["expired", "cancelled"].includes(trial.status);
  if (!trial.live && !over) return null;

  const cancelled = !!trial.cancel_at_period_end || trial.status === "cancelled";
  const frozen = !!trial.commitments_frozen;
  const committed = trial.committed_skus || [];

  const removeSku = async (sku) => {
    setBusy(sku);
    try {
      const r = await api.delete(`/trial/commit/${encodeURIComponent(sku)}`);
      setTrial(r.data?.trial);
      toast.success("Taken off your day-14 bill.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove that.");
    } finally { setBusy(null); }
  };

  const dropBundle = async () => {
    setBusy("bundle");
    try {
      const r = await api.delete("/trial/commit-bundle");
      setTrial(r.data?.trial);
      toast.success("The bundle is off your day-14 bill.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not drop the bundle.");
    } finally { setBusy(null); }
  };

  const cancelTrial = async () => {
    setBusy("cancel");
    try {
      const r = await api.post("/trial/cancel", {});
      toast.success(r.data?.message || "Your trial is cancelled.");
      const fresh = await api.get("/trial/status");
      setTrial(fresh.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel the trial.");
    } finally { setBusy(null); }
  };

  const convertNow = async () => {
    setBusy("convert");
    try {
      const r = await api.post("/trial/convert-now", {});
      toast.success(r.data?.message || "Your subscription has started.");
      const fresh = await api.get("/trial/status");
      setTrial(fresh.data);
      try { window.dispatchEvent(new CustomEvent("profile-updated")); } catch { /* fine */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the subscription.");
    } finally { setBusy(null); }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const r = await api.post("/stripe/platform/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open the billing portal.");
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border hairline rounded-xl p-6 shadow-sm" data-testid="billing-trial-card">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
          over ? "bg-[#F0F1EE] text-[#6B6A68]" : "bg-[#E8EBE5] text-[#4A5D4E]"
        }`}>
          {over ? <Lock size={20} weight="duotone" /> : <Clock size={20} weight="duotone" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif-display text-xl text-[#1C1B1A]">Your free trial</h3>
          <p className="text-sm text-[#6B6A68] mt-0.5" data-testid="billing-trial-status">
            {over
              ? `Ended ${trialDate(trial.ends_at)} — the Creator Pro tools are locked and nothing was charged.`
              : cancelled
                ? `Cancelled — full access until ${trialDate(trial.ends_at)}, and you will not be charged.`
                : `Active — ${trial.days_left} day${trial.days_left === 1 ? "" : "s"} left, ends ${trialDate(trial.ends_at)}.`}
          </p>
        </div>
      </div>

      {cancelled && !!committed.length && (
        <div className="rounded-md border border-[#B5791F]/30 bg-[#FBF2E2] px-3.5 py-3 mb-4" data-testid="billing-trial-void-banner">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A5A00]">Void — not being charged</div>
          <p className="text-[13px] text-[#1C1B1A] mt-1">
            These Pro selections are <strong>void</strong> because the trial is cancelled. They are not
            pending, nothing will be billed for them, and the tools simply lock when the trial ends.
          </p>
          <ul className="mt-2 space-y-1">
            {committed.map((row) => (
              <li key={row.sku} className="text-[12px] text-[#6B6A68] line-through flex items-center justify-between gap-3"
                  data-testid={`billing-trial-void-${row.sku}`}>
                <span>{row.name}</span><span className="tabular-nums">{money(row.price_cents)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!over && !cancelled && (
        <div className="rounded-md border hairline bg-[#FDFCF8] px-3.5 py-3" data-testid="billing-trial-preview">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B6A68]">
            What bills on {trialDate(trial.ends_at)}
          </div>
          <div className="mt-2 space-y-1.5">
            <div className="flex items-center justify-between gap-3 text-[13px] text-[#1C1B1A]">
              <span>Creator subscription</span>
              <span className="tabular-nums">{money(trial.subscription_cents)}/mo</span>
            </div>
            {trial.committed_bundle && (
              <div className="flex items-center justify-between gap-3 text-[13px] text-[#1C1B1A]">
                <span>Creator Pro — the full bundle</span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums">{money(trial.committed_cents)}</span>
                  {!frozen && (
                    <button type="button" onClick={dropBundle} disabled={busy === "bundle"}
                      data-testid="billing-trial-drop-bundle"
                      className="text-[#6B6A68] hover:text-[#A33A3A] disabled:opacity-50" title="Remove the bundle">
                      <X size={13} />
                    </button>
                  )}
                </span>
              </div>
            )}
            {!trial.committed_bundle && committed.map((row) => (
              <div key={row.sku} className="flex items-center justify-between gap-3 text-[13px] text-[#1C1B1A]"
                   data-testid={`billing-trial-item-${row.sku}`}>
                <span>
                  {row.name}
                  {row.removable === false && (
                    <span className="text-[11px] text-[#6B6A68]"> · included for every creator</span>
                  )}
                </span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums">{money(row.price_cents)}</span>
                  {!frozen && row.removable !== false && (
                    <button type="button" onClick={() => removeSku(row.sku)} disabled={busy === row.sku}
                      data-testid={`billing-trial-remove-${row.sku}`}
                      className="text-[#6B6A68] hover:text-[#A33A3A] disabled:opacity-50" title={`Remove ${row.name}`}>
                      <X size={13} />
                    </button>
                  )}
                </span>
              </div>
            ))}
            {!committed.length && (
              <div className="text-[12px] text-[#6B6A68]">
                No Pro tools on the bill. They stay free during the trial and lock afterwards unless you keep them.
              </div>
            )}
          </div>
          <div className="mt-2.5 pt-2.5 border-t hairline flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#6B6A68]">Total that day</span>
            <span className="font-serif-display text-2xl tabular-nums text-[#1C1B1A]" data-testid="billing-trial-total">
              {money(trial.running_total_cents)}
            </span>
          </div>
          {frozen && (
            <p className="text-[11px] text-[#8A5A00] mt-2" data-testid="billing-trial-frozen">
              This list is locked in — it freezes an hour before the trial ends so the bill can't change
              underneath you.
            </p>
          )}
        </div>
      )}

      <div className="mt-4">
        <TrialCreditNote trial={trial} tone="light" testId="billing-trial-credit-note" />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {trial.live && !cancelled && (
          <>
            <button type="button" onClick={convertNow} disabled={!!busy} data-testid="billing-trial-convert"
              className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60 transition-colors">
              {busy === "convert" ? "Starting…" : "Start paying now"}
            </button>
            <button type="button" onClick={cancelTrial} disabled={!!busy} data-testid="billing-trial-cancel"
              className="text-sm px-4 py-2 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#A33A3A] hover:border-[#A33A3A]/40 disabled:opacity-60 transition-colors">
              {busy === "cancel" ? "Cancelling…" : "Cancel trial"}
            </button>
          </>
        )}
        {trial.live && (
          <button type="button" onClick={openPortal} disabled={!!busy} data-testid="billing-trial-portal"
            className="text-sm px-4 py-2 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5 disabled:opacity-60">
            <ArrowSquareOut size={14} /> Manage card in Stripe
          </button>
        )}
      </div>
    </div>
  );
}
